/**
 * RequestAccessInterstitial — what a 403 dead-end SHOULD be.
 *
 * Today a locked session/patient/document ends in a useless "Retry" that can
 * never succeed. This replaces it with a way forward: it names what is locked,
 * shows what you CAN still reach (so it is not a wall), and turns the block into
 * a one-tap access request with an optional justification. No paternalistic
 * copy — the block is stated plainly, the nurse decides.
 *
 * Presentational: the request itself fires through `onRequest`; outcome is driven
 * by the `status` prop (mirrors the org access-request inline outcomes — sent /
 * already pending / already authorized / error), so the connected layer maps its
 * mutation state onto these without this component knowing the API.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Clock, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { resourceIcon, resourceTypeKey } from './accessMeta'
import type { ResourceRef } from './types'

export type RequestAccessStatus =
  | 'idle'
  | 'pending'
  | 'sent'
  | 'already_pending'
  | 'already_authorized'
  | 'error'

export interface RequestAccessInterstitialProps {
  /** The resource that is locked. */
  resource: ResourceRef
  /** Sibling resources the caller CAN reach — the "not a dead end" list. */
  visible?: ResourceRef[]
  onRequest: (justification?: string) => void
  onVisibleSelect?: (resource: ResourceRef) => void
  status?: RequestAccessStatus
  /** Verbatim server error, shown only when status === 'error'. */
  errorMessage?: string
  className?: string
}

export function RequestAccessInterstitial({
  resource,
  visible,
  onRequest,
  onVisibleSelect,
  status = 'idle',
  errorMessage,
  className,
}: RequestAccessInterstitialProps) {
  const { t } = useTranslation()
  const [showJustify, setShowJustify] = useState(false)
  const [justification, setJustification] = useState('')

  const ResourceIcon = resourceIcon(resource.type)

  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-md flex-col items-center gap-5 rounded-[18px] border border-neutral-200 bg-white p-6 text-center',
        className,
      )}
    >
      <span
        className="grid h-12 w-12 place-items-center rounded-full bg-neutral-100 text-neutral-500"
        aria-hidden="true"
      >
        <Lock className="h-5 w-5" />
      </span>

      <div className="space-y-1.5">
        <h2 className="text-[19px] font-semibold tracking-[-0.01em] text-neutral-900">
          {t('access.request.title')}
        </h2>
        <p className="inline-flex items-center justify-center gap-1.5 text-[15px] text-neutral-600">
          <ResourceIcon className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
          <span className="font-semibold text-neutral-800">{resource.label}</span>
        </p>
        <p className="text-[13px] text-neutral-500">
          {t('access.request.lockedType', {
            type: t(resourceTypeKey(resource.type), { count: 1, defaultValue: resource.type }),
          })}
        </p>
      </div>

      {/* What you CAN see — so the block is a fork, not a wall. */}
      {visible && visible.length > 0 && (
        <div className="w-full space-y-1.5 text-left">
          <p className="text-[12px] font-semibold uppercase tracking-wide text-neutral-400">
            {t('access.request.canSee')}
          </p>
          <ul className="space-y-1">
            {visible.map((v) => {
              const VIcon = resourceIcon(v.type)
              const inner = (
                <>
                  <VIcon className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">{v.label}</span>
                </>
              )
              return (
                <li key={`${v.type}:${v.id}`}>
                  {onVisibleSelect ? (
                    <button
                      type="button"
                      onClick={() => onVisibleSelect(v)}
                      className="flex min-h-[44px] w-full items-center gap-2 rounded-[8px] px-2.5 text-[14px] text-primary transition active:scale-[0.98] hover:bg-primary-50"
                    >
                      {inner}
                    </button>
                  ) : (
                    <span className="flex min-h-[44px] w-full items-center gap-2 rounded-[8px] px-2.5 text-[14px] text-neutral-700">
                      {inner}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* Outcome states mirror the org access-request inline flow. */}
      {status === 'sent' ? (
        <p className="inline-flex items-center gap-1.5 rounded-full bg-tone-ok-tint px-3 py-1.5 text-[14px] font-semibold text-tone-ok">
          <Check className="h-4 w-4" aria-hidden="true" />
          {t('access.request.sent')}
        </p>
      ) : status === 'already_authorized' ? (
        <p className="inline-flex items-center gap-1.5 rounded-full bg-tone-ok-tint px-3 py-1.5 text-[14px] font-semibold text-tone-ok">
          <Check className="h-4 w-4" aria-hidden="true" />
          {t('access.request.alreadyAuthorized')}
        </p>
      ) : status === 'already_pending' ? (
        <p className="inline-flex items-center gap-1.5 rounded-full bg-citation-50 px-3 py-1.5 text-[14px] font-semibold text-citation-700">
          <Clock className="h-4 w-4" aria-hidden="true" />
          {t('access.request.alreadyPending')}
        </p>
      ) : (
        <div className="w-full space-y-2">
          {showJustify && (
            <Textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder={t('access.request.justificationPlaceholder')}
              className="min-h-[64px] text-left text-[15px]"
            />
          )}
          <div className="flex flex-col items-center gap-2">
            <Button
              type="button"
              size="pill"
              disabled={status === 'pending'}
              onClick={() => onRequest(justification.trim() || undefined)}
              className="min-h-11 w-full"
            >
              {status === 'pending' ? t('access.request.sending') : t('access.request.request')}
            </Button>
            {!showJustify && (
              <button
                type="button"
                onClick={() => setShowJustify(true)}
                className="min-h-[44px] text-[13px] font-semibold text-primary transition active:scale-95"
              >
                {t('access.request.addJustification')}
              </button>
            )}
          </div>
          {status === 'error' && errorMessage && (
            <p className="rounded-[8px] bg-error-50 px-3 py-2 text-[13px] text-error-700">
              {errorMessage}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
